'use client';

import { FC, useCallback, useMemo } from 'react';
import CustomizingEntityDialog from '@/components/customizing/CustomizingEntityDialog';
import { useCreateMaterial } from '@/hooks/useCreateMaterial';
import { emptyInlineMaterialRecord } from '@/lib/materials/materialEntityConfig';
import type { MaterialDto } from '@/api/ApiClient';
import type { CustomizingRecord } from '@/types/customizing';

export type MaterialCreateDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (material: MaterialDto, freshMaterials: MaterialDto[]) => void;
  /** When true, create a substrate / bulk-precursor catalog material (Material.IsSubstrate). */
  forSubstrate?: boolean;
};

/**
 * Shared inline material create dialog (name + label only, KO MaterialPopup parity).
 */

export const MaterialCreateDialog: FC<MaterialCreateDialogProps> = ({
  open,
  onClose,
  onCreated,
  forSubstrate = false,
}) => {
  const { inlineMaterialEntityConfig, createMaterial } = useCreateMaterial();
  const emptyRecord = useMemo(() => emptyInlineMaterialRecord(forSubstrate), [forSubstrate]);
  const handleSave = useCallback(
    async (record: CustomizingRecord) => {
      const result = await createMaterial(record as Record<string, unknown>, { forSubstrate });
      if (result) {
        onCreated(result.material, result.freshMaterials);
        onClose();
      }
    },
    [createMaterial, forSubstrate, onClose, onCreated]
  );
  if (!open) {
    return null;
  }
  return (
    <CustomizingEntityDialog
      key={forSubstrate ? 'material-create-inline-substrate' : 'material-create-inline-film'}
      open
      entityType="materials"
      entityConfig={inlineMaterialEntityConfig}
      record={emptyRecord as CustomizingRecord}
      isEdit={false}
      onSave={handleSave}
      onClose={onClose}
    />
  );
};
